import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';

import UploadStep from '../components/import/UploadStep';
import MappingStep from '../components/import/MappingStep';
import PreviewStep from '../components/import/PreviewStep';
import CompleteStep from '../components/import/CompleteStep';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ImportResourcesPage = () => {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [previewData, setPreviewData] = useState(null);
  const [selectedResources, setSelectedResources] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [defaultNeighborhood, setDefaultNeighborhood] = useState('');
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [editingIdx, setEditingIdx] = useState(null);
  const [sdohCategories, setSdohCategories] = useState([]);

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const [nResp, cResp] = await Promise.all([
          axios.get(`${API_URL}/public/neighborhoods`),
          axios.get(`${API_URL}/settings/categories`, { headers: getAuthHeaders() }).catch(() => null)
        ]);
        setNeighborhoods(nResp.data.neighborhoods || []);
        if (cResp?.data?.sdoh_categories) {
          setSdohCategories(cResp.data.sdoh_categories);
        } else {
          setSdohCategories(['Food Security', 'Housing & Shelter', 'Healthcare Access', 'Mental Health', 'Substance Use', 'Employment / Income', 'Education / ESL', 'Legal / Immigration', 'Childcare / Family Support', 'Transportation', 'Violence / Safety', 'Utilities / Technology']);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error.message);
        setNeighborhoods(['Roxbury', 'Dorchester', 'Mattapan', 'East Boston', 'Other']);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      const validTypes = ['.csv', '.xlsx', '.xls'];
      if (validTypes.some(type => droppedFile.name.toLowerCase().endsWith(type))) {
        setFile(droppedFile);
      } else {
        toast.error('Please upload a CSV or Excel file');
      }
    }
  }, []);

  const analyzeFile = async () => {
    if (!file) return;
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API_URL}/import/analyze`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setAnalysisResult(response.data);
      setMappings(response.data.mappings);
      setStep(2);
      toast.success('File analyzed! Review the suggested mappings.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to analyze file');
    } finally {
      setLoading(false);
    }
  };

  const updateMapping = (sourceColumn, targetField) => {
    setMappings(prev => {
      const existing = prev.find(m => m.source_column === sourceColumn);
      if (existing) {
        if (targetField === '' || targetField === '__skip__') return prev.filter(m => m.source_column !== sourceColumn);
        return prev.map(m => m.source_column === sourceColumn ? { ...m, target_field: targetField } : m);
      } else if (targetField !== '' && targetField !== '__skip__') {
        return [...prev, { source_column: sourceColumn, target_field: targetField, confidence: 1.0 }];
      }
      return prev;
    });
  };

  const updatePreviewResource = (idx, field, value) => {
    setPreviewData(prev => {
      const updated = { ...prev, resources: [...prev.resources] };
      updated.resources[idx] = { ...updated.resources[idx], [field]: value };
      return updated;
    });
  };

  const generatePreview = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/import/preview`, {
        mappings, file_data: analysisResult.file_data, default_neighborhood: defaultNeighborhood || null
      }, { headers: getAuthHeaders(), timeout: 30000 });
      
      const { enrichment_job_id } = response.data;
      setPreviewData(response.data);
      setSelectedResources(response.data.resources.map((_, idx) => idx));
      setStep(3);
      
      if (enrichment_job_id) {
        toast.info('AI is analyzing resources... categories and organizations will fill in automatically.');
        const pollEnrichment = async () => {
          for (let attempt = 0; attempt < 60; attempt++) {
            await new Promise(r => setTimeout(r, 3000));
            try {
              const statusResp = await axios.get(`${API_URL}/import/enrichment-status/${enrichment_job_id}`, { headers: getAuthHeaders(), timeout: 10000 });
              if (statusResp.data.status === 'complete') {
                setPreviewData(prev => ({ ...prev, resources: statusResp.data.resources, warnings: statusResp.data.warnings || prev.warnings }));
                toast.success('AI enrichment complete! Review categories and organizations.');
                return;
              }
            } catch (e) {
              console.error('Enrichment poll error:', e.message);
            }
          }
          toast.warning('AI enrichment is taking longer than expected. You can edit fields manually.');
        };
        pollEnrichment();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  const executeImport = async () => {
    setLoading(true);
    try {
      const resourcesToImport = selectedResources.map(idx => previewData.resources[idx]);
      const response = await axios.post(`${API_URL}/import/execute`, resourcesToImport, { headers: getAuthHeaders() });
      setImportResult(response.data);
      setStep(4);
      toast.success(`Successfully imported ${response.data.imported} resources!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import resources');
    } finally {
      setLoading(false);
    }
  };

  const resetImport = () => {
    setStep(1);
    setFile(null);
    setAnalysisResult(null);
    setMappings([]);
    setPreviewData(null);
    setImportResult(null);
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]">
      <header className="sticky top-0 z-40 bg-[#FAF8F5]/95 backdrop-blur border-b border-[#E0D6C8]">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn" className="rounded-full hover:bg-[#F3EFE9]">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <span className="font-['Manrope'] text-base tracking-tight mr-1"><span className="font-extrabold text-[#5C4023]">617</span><span className="font-medium text-[#C87D3E]">Reach</span></span>
                <h1 className="text-lg font-bold text-[#1A1A1A] font-['Manrope']">Import Resources</h1>
              </div>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              {[1, 2, 3, 4].map((s) => (
                <div key={s} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= s ? 'bg-[#5C4023] text-white' : 'bg-[#F3EFE9] text-[#888888]'}`}>
                    {step > s ? <CheckCircle2 className="w-4 h-4" /> : s}
                  </div>
                  {s < 4 && <div className={`w-8 h-0.5 ${step > s ? 'bg-[#5C4023]' : 'bg-[#E0D6C8]'}`} />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        {step === 1 && <UploadStep file={file} setFile={setFile} loading={loading} onAnalyze={analyzeFile} onFileDrop={handleFileDrop} />}
        {step === 2 && analysisResult && (
          <MappingStep
            analysisResult={analysisResult} mappings={mappings} onUpdateMapping={updateMapping}
            defaultNeighborhood={defaultNeighborhood} setDefaultNeighborhood={setDefaultNeighborhood}
            neighborhoods={neighborhoods} loading={loading}
            onBack={() => setStep(1)} onPreview={generatePreview}
          />
        )}
        {step === 3 && previewData && (
          <PreviewStep
            previewData={previewData} selectedResources={selectedResources}
            editingIdx={editingIdx} setEditingIdx={setEditingIdx}
            sdohCategories={sdohCategories} loading={loading}
            onToggleSelection={(idx) => setSelectedResources(prev => prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx])}
            onSelectAll={() => setSelectedResources(previewData.resources.map((_, idx) => idx))}
            onDeselectAll={() => setSelectedResources([])}
            onUpdateResource={updatePreviewResource}
            onBack={() => setStep(2)} onExecuteImport={executeImport}
          />
        )}
        {step === 4 && importResult && <CompleteStep importResult={importResult} onImportMore={resetImport} />}
      </main>
    </div>
  );
};

export default ImportResourcesPage;
